import { createContext, useContext, useLayoutEffect, useState, type ReactNode } from 'react';

/** What the person chose: light, dark, or the default ("mode", which is light now that pages have no modes). */
export type ThemePref = 'mode' | 'light' | 'dark';
export type Theme = 'light' | 'dark';
const KEY = 'mv.theme';

const resolve = (pref: ThemePref): Theme => (pref === 'dark' ? 'dark' : 'light');

const ThemeContext = createContext<{ pref: ThemePref; theme: Theme; setPref: (p: ThemePref) => void }>({
  pref: 'mode', theme: 'light', setPref: () => undefined,
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(() => {
    try {
      const v = localStorage.getItem(KEY);
      return v === 'light' || v === 'dark' || v === 'mode' ? v : 'mode';
    } catch {
      return 'mode';
    }
  });
  const theme = resolve(pref);

  // On <html>, so menus, peeks and suggestions rendered in portals inherit the tokens too.
  useLayoutEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const setPref = (p: ThemePref) => {
    setPrefState(p);
    try {
      localStorage.setItem(KEY, p);
    } catch {
      // not persisted
    }
  };
  return <ThemeContext.Provider value={{ pref, theme, setPref }}>{children}</ThemeContext.Provider>;
}

export function useThemePref() {
  return useContext(ThemeContext);
}

/** The theme in use. The old pages still pass their mode; it no longer matters. */
export function useTheme(_mode?: 'search' | 'monitor'): Theme {
  return useContext(ThemeContext).theme;
}
